import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { ActionButton } from '../../types/action-button';
import { BroadcastService } from '../broadcast/broadcast.service';
import { CommonEvents } from '../../enums/common-events.enum';
import { ReplaySubject, Subject } from 'rxjs';
import { RouteInterceptorService } from '../route-interceptor/route-interceptor.service';

enum WidthThreshold {
    Xs = 340,
    S = 440,
    M = 540,
    L = 640
}

@Injectable({
    providedIn: 'root'
})
export class ContextButtonService {

    public buttons: ActionButton[];
    public customBackButton: ActionButton;
    public isBackEnabled: boolean;
    public isCloseButton = false;
    public isCustomBackEnabled = false;
    public isDefaultSearchButtonVisible = true;
    public showNavPanel = true;
    public toolbarHidden: boolean;

    private parentRoute: string;
    private parentParams: object;

    private navbarHeightSubject: Subject<number> = new ReplaySubject<number>(1);
    private navbarWidthSubject: Subject<number> = new ReplaySubject<number>(1);
    private maxVisibleButtonsCountSubject: Subject<number> = new ReplaySubject<number>(1);

    constructor(
        private broadcastService: BroadcastService,
        private router: Router,
        private routeInterceptor: RouteInterceptorService
    ) {
    }

    public changeDefaultSearchButtonVisibility(isVisible: boolean){
        this.isDefaultSearchButtonVisible = isVisible;
        setTimeout(() => this.broadcastService.broadcast(CommonEvents.ButtonPanel, true));
    }

    public changeIsCustomBackEnabled(isEnabled: boolean){
        this.isCustomBackEnabled = isEnabled;
    }

    public setButtons(buttons: ActionButton[]) {
        this.buttons = buttons;
        setTimeout(() => this.broadcastService.broadcast(CommonEvents.ButtonPanel, true));
    }

    public setCustomBackButton(button: ActionButton) {
        this.customBackButton = button;
        setTimeout(() => this.broadcastService.broadcast(CommonEvents.ButtonPanel, true));
    }

    public clearButtons() {
        this.buttons = null;
        this.customBackButton = null;
        setTimeout(() => this.broadcastService.broadcast(CommonEvents.ButtonPanel, false));
    }

    public hasContextButtons(): boolean {
        return this.buttons != null && this.buttons.some(button => !button.isHidden());
    }

    public setParentRoute(route: string, params: object) {
        this.isBackEnabled = true;
        this.parentRoute = route;
        this.parentParams = params;
    }

    public goBack() {
        const route = this.getPreviousUrl();
        this.router.navigate([route.url], route.navigationExtras);
        this.isBackEnabled = false;
        this.parentRoute = null;
        this.parentParams = null;
    }

    private getPreviousUrl() {
        const routeObject = this.routeInterceptor.goBackInRouteHistory();

        const navigationExtras: NavigationExtras = {
            queryParams: routeObject.queryParams,
            fragment: routeObject.fragment,
        };
        return { url: routeObject.url, navigationExtras };
    }

    /**
     * Shows bottom panel without custom buttons,
     * if back button is set only that button will be shown
     */
    public showEmptyPanel() {
        this.setButtons([]);
    }

    public setNavbarPanelHeight(height: number) {
        this.navbarHeightSubject.next(height);
    }

    public setNavbarPanelWidth(width: number) {
        this.navbarWidthSubject.next(width);
        this.changeOverflowThreshold(width);
    }

    private changeOverflowThreshold(width: number) {
        if (width < WidthThreshold.Xs) {
            this.maxVisibleButtonsCountSubject.next(1);
        } else if (width >= WidthThreshold.Xs && width < WidthThreshold.S) {
            this.maxVisibleButtonsCountSubject.next(2);
        } else if (width >= WidthThreshold.S && width < WidthThreshold.M) {
            this.maxVisibleButtonsCountSubject.next(3);
        } else if (width >= WidthThreshold.M && width < WidthThreshold.L) {
            this.maxVisibleButtonsCountSubject.next(4);
        } else {
            this.maxVisibleButtonsCountSubject.next(5);
        }
    }

    public getNavbarPanelHeightObservable() {
        return this.navbarHeightSubject.asObservable();
    }

    public getNavbarPanelWidthObservable() {
        return this.navbarWidthSubject.asObservable();
    }

    public maxVisibleButtonsCountObservable() {
        return this.maxVisibleButtonsCountSubject.asObservable();
    }

}
